import { useState } from 'react'
import { useEditor, type Tool } from '@/state/store'
import { SNAP_INCREMENTS } from '@/model/defaults'
import { ShareModal } from '@/components/ShareModal'

const TOOLS: { tool: Tool; label: string; key: string; icon: string }[] = [
  { tool: 'select', label: 'Select', key: 'V', icon: '⬚' },
  { tool: 'space-rect', label: 'Draw (rect)', key: 'R', icon: '▭' },
  { tool: 'space-poly', label: 'Draw (pen)', key: 'P', icon: '⬡' },
  { tool: 'opening', label: 'Opening', key: 'D', icon: '🚪' },
  { tool: 'pan', label: 'Pan', key: 'H', icon: '✋' },
]

/** Tools still allowed while viewing a read-only share (navigation only). */
const VIEW_TOOLS = new Set<Tool>(['select', 'pan'])

export function Toolbar() {
  const tool = useEditor((s) => s.tool)
  const setTool = useEditor((s) => s.setTool)
  const undo = useEditor((s) => s.undo)
  const redo = useEditor((s) => s.redo)
  const canUndo = useEditor((s) => s.past.length > 0)
  const canRedo = useEditor((s) => s.future.length > 0)
  const newProject = useEditor((s) => s.newProject)
  const setMode = useEditor((s) => s.setMode)
  const snap = useEditor((s) => s.project.settings.gridSnap)
  const update = useEditor((s) => s.update)
  const name = useEditor((s) => s.project.name)
  const readOnly = useEditor((s) => s.readOnly)
  const [shareOpen, setShareOpen] = useState(false)

  return (
    <header className="toolbar">
      <div className="toolbar-group brand">
        <span className="logo">⌂</span>
        <input
          className="project-name"
          value={name}
          onChange={(e) => update((d) => void (d.name = e.target.value), { transient: true })}
          spellCheck={false}
          readOnly={readOnly}
        />
      </div>

      <div className="toolbar-group">
        {TOOLS.map((t) => (
          <button
            key={t.tool}
            className={`tool-btn${tool === t.tool ? ' active' : ''}`}
            onClick={() => setTool(t.tool)}
            disabled={readOnly && !VIEW_TOOLS.has(t.tool)}
            title={`${t.label} (${t.key})`}
          >
            <span className="tool-icon">{t.icon}</span>
            <span className="tool-label">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="toolbar-group">
        <label className="snap-label">Snap</label>
        <select
          className="snap-select"
          value={snap}
          disabled={readOnly}
          onChange={(e) => update((d) => void (d.settings.gridSnap = Number(e.target.value)))}
        >
          {SNAP_INCREMENTS.map((inc) => (
            <option key={inc} value={inc}>
              {inc < 1 ? `${Math.round(inc * 12)} in` : `${inc} ft`}
            </option>
          ))}
        </select>
      </div>

      <div className="toolbar-group right">
        {!readOnly && (
          <>
            <button className="ghost-btn" onClick={undo} disabled={!canUndo} title="Undo (⌘Z)">
              ↶
            </button>
            <button className="ghost-btn" onClick={redo} disabled={!canRedo} title="Redo (⇧⌘Z)">
              ↷
            </button>
            <button className="ghost-btn" onClick={() => newProject()} title="New project">
              New
            </button>
            <button className="ghost-btn" onClick={() => setShareOpen(true)} title="Share a read-only link">
              Share
            </button>
          </>
        )}
        <button
          className="primary-btn"
          onClick={() => setMode('visualize')}
          title="Switch to 3D Visualize"
        >
          Visualize ▶
        </button>
      </div>

      {shareOpen && <ShareModal onClose={() => setShareOpen(false)} />}
    </header>
  )
}
